import { getAuthToken } from "../token";
import { BASE_URL, ADMIN_URL } from "../api";

export const getApiHeaders = (isFormData = false) => {
    const token = getAuthToken();

    return {
        Authorization: token ? `Bearer ${token}` : undefined,
        ...(isFormData
            ? { "Content-Type": "multipart/form-data" }
            : { "Content-Type": "application/json" }),
    };
};

// when it is used in axios,
// headers: getApiHeaders(), // false = application/json
// headers: getApiHeaders(true), // true = multipart/form-data


export const shortAddress = (address) => {
    if (!address) return "";

    return `${address.slice(0, 10)}...${address.slice(-8)}`;
};

export const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

export const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

/**
 * Returns blockchain explorer URL for wallet address.
 */
export const getExplorerAddressUrl = (network = "", address = "") => {
    if (!address || typeof address !== "string") return null;
    const addr = address.trim();
    if (!addr) return null;
    const net = String(network || "").toUpperCase().trim();

    if (net.includes("TRC") || net.includes("TRON") || addr.startsWith("T")) {
        return `https://tronscan.org/#/address/${addr}`;
    }
    if (net.includes("BEP") || net.includes("BSC") || net.includes("BINANCE") || net.includes("BNB")) {
        return `https://bscscan.com/address/${addr}`;
    }
    if (net.includes("ERC") || net.includes("ETH") || net.includes("ETHEREUM")) {
        return `https://etherscan.io/address/${addr}`;
    }
    if (net.includes("POLYGON") || net.includes("MATIC")) {
        return `https://polygonscan.com/address/${addr}`;
    }
    if (net.includes("SOL")) {
        return `https://solscan.io/account/${addr}`;
    }
    if (net.includes("BTC") || net.includes("BITCOIN")) {
        return `https://www.blockchain.com/explorer/addresses/btc/${addr}`;
    }
    if (addr.startsWith("0x")) {
        return `https://bscscan.com/address/${addr}`;
    }
    return null;
};

/**
 * Returns blockchain explorer URL for transaction hash.
 */
export const getExplorerTxUrl = (network = "", txHash = "") => {
    if (!txHash || typeof txHash !== "string") return null;
    const hash = txHash.trim();
    if (!hash) return null;
    const net = String(network || "").toUpperCase().trim();

    if (net.includes("TRC") || net.includes("TRON")) {
        return `https://tronscan.org/#/transaction/${hash}`;
    }
    if (net.includes("BEP") || net.includes("BSC") || net.includes("BINANCE") || net.includes("BNB")) {
        return `https://bscscan.com/tx/${hash}`;
    }
    if (net.includes("ERC") || net.includes("ETH") || net.includes("ETHEREUM")) {
        return `https://etherscan.io/tx/${hash}`;
    }
    if (net.includes("POLYGON") || net.includes("MATIC")) {
        return `https://polygonscan.com/tx/${hash}`;
    }
    if (net.includes("SOL")) {
        return `https://solscan.io/tx/${hash}`;
    }
    if (net.includes("BTC") || net.includes("BITCOIN")) {
        return `https://www.blockchain.com/explorer/transactions/btc/${hash}`;
    }
    if (hash.startsWith("0x")) {
        return `https://bscscan.com/tx/${hash}`;
    }
    // Default fallback based on network or general bscscan
    return `https://bscscan.com/tx/${hash}`;
};

/**
 * Returns explorer name (e.g., Tronscan, BscScan, Etherscan).
 */
export const getExplorerName = (network = "", address = "") => {
    const net = String(network || "").toUpperCase().trim();
    const addr = String(address || "").trim();
    if (net.includes("TRC") || net.includes("TRON") || addr.startsWith("T")) {
        return "Tronscan";
    }
    if (net.includes("BEP") || net.includes("BSC") || net.includes("BINANCE") || addr.startsWith("0x")) {
        return "BscScan";
    }
    if (net.includes("ERC") || net.includes("ETH") || net.includes("ETHEREUM")) {
        return "Etherscan";
    }
    if (net.includes("POLYGON") || net.includes("MATIC")) {
        return "Polygonscan";
    }
    if (net.includes("SOL")) {
        return "Solscan";
    }
    return "Blockchain Explorer";
};


/**
 * Resolves attachment URL for admin replies (served from admin panel / ticket_files).
 */
export const getAdminAttachmentUrl = (path) => {
    if (!path || typeof path !== "string") return "";
    const trimmed = path.trim();
    if (!trimmed || trimmed === "null" || trimmed === "undefined") return "";

    if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) {
        return trimmed;
    }

    let urlPath = trimmed;

    // Replace old admin domain if present
    if (urlPath.includes("apt-admin.apextrade.online")) {
        urlPath = urlPath.replace(/https?:\/\/apt-admin\.apextrade\.online/g, "https://adminpanel.apextrade.online");
    }

    // Already a full URL
    if (urlPath.startsWith("http://") || urlPath.startsWith("https://")) {
        return encodeURI(urlPath);
    }

    const adminBase = (ADMIN_URL || "https://adminpanel.apextrade.online/ticket_files/").replace(/\/+$/, "");
    const adminOrigin = adminBase.replace(/\/ticket_files$/, "");

    const normalizedPath = urlPath.startsWith("/") ? urlPath : `/${urlPath}`;
    if (normalizedPath.startsWith("/ticket_files/")) {
        return encodeURI(`${adminOrigin}${normalizedPath}`);
    }

    return encodeURI(`${adminOrigin}/ticket_files${normalizedPath}`);
};

/**
 * Resolves media/document/image relative path with current BASE_URL.
 * Supports paths like "/support_media/raise_ticket_docs/...", "/kyc_media/...", etc.
 */
export const getFullMediaUrl = (path) => {
    if (!path || typeof path !== "string") return "";
    const trimmed = path.trim();
    if (!trimmed || trimmed === "null" || trimmed === "undefined") return "";

    if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) {
        return trimmed;
    }

    let urlPath = trimmed;

    // Replace old admin domain if present
    if (urlPath.includes("apt-admin.apextrade.online")) {
        urlPath = urlPath.replace(/https?:\/\/apt-admin\.apextrade\.online/g, "https://adminpanel.apextrade.online");
    }

    // Preserve admin panel URLs directly
    if (
        urlPath.startsWith("https://adminpanel.apextrade.online") ||
        urlPath.startsWith("http://adminpanel.apextrade.online")
    ) {
        return encodeURI(urlPath);
    }

    // Strip legacy localhost or former domain
    if (
        urlPath.startsWith("http://127.0.0.1:") ||
        urlPath.startsWith("http://localhost:") ||
        urlPath.startsWith("https://crmapi.blockcryp.com")
    ) {
        urlPath = urlPath.replace(/^https?:\/\/[^/]+/, "");
    }

    if (urlPath.startsWith("http://") || urlPath.startsWith("https://")) {
        return encodeURI(urlPath);
    }

    // If path is under ticket_files, route to admin url
    if (urlPath.startsWith("/ticket_files/") || urlPath.startsWith("ticket_files/")) {
        return getAdminAttachmentUrl(urlPath);
    }

    const cleanPath = urlPath.startsWith("/") ? urlPath : `/${urlPath}`;
    return encodeURI(`${BASE_URL}${cleanPath}`);
};

